import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, FileText } from 'lucide-react';
import { pagesApi, Page } from '@/services/pagesApi';
import toast from 'react-hot-toast';

const TYPE_LABELS: Record<string, string> = {
  'quick-link': 'Hızlı Link',
  'support': 'Destek',
  'blog': 'Blog',
};

const TYPE_COLORS: Record<string, string> = {
  'quick-link': 'bg-blue-100 text-blue-800',
  'support': 'bg-purple-100 text-purple-800',
  'blog': 'bg-rose-100 text-rose-700',
};

export const PagesList: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'blog' | 'footer'>('all');

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const data = await pagesApi.getAll();
      setPages(data);
    } catch (error) {
      toast.error('Sayfalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`"${title}" sayfasını silmek istediğinize emin misiniz?`)) return;
    try {
      await pagesApi.remove(id);
      toast.success('Sayfa silindi');
      loadPages();
    } catch {
      toast.error('Sayfa silinemedi');
    }
  };

  const handleToggleStatus = async (page: Page) => {
    try {
      const newStatus = page.status === 'active' ? 'inactive' : 'active';
      await pagesApi.update(page.id, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Sayfa aktifleştirildi' : 'Sayfa pasifleştirildi');
      loadPages();
    } catch {
      toast.error('Durum güncellenemedi');
    }
  };

  const filteredPages = pages.filter((page) => {
    if (filter === 'blog') {
      return page.type === 'blog';
    }

    if (filter === 'footer') {
      return page.type !== 'blog';
    }

    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin text-4xl">⏳</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">Sayfalar</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Footer sayfaları ve SEO blog içeriklerini yönetin</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
            {[
              { key: 'all', label: 'Tümü' },
              { key: 'blog', label: 'Blog' },
              { key: 'footer', label: 'Footer' },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key as typeof filter)}
                className={`rounded-lg px-3 py-2 text-sm transition ${filter === item.key ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300'}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => navigate('/admin/pages/new')}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Yeni İçerik
          </button>
        </div>
      </div>

      {filteredPages.length === 0 ? (
        <div className="card text-center py-12">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">Bu filtre için içerik bulunmuyor</p>
          <button
            onClick={() => navigate('/admin/pages/new')}
            className="btn btn-primary mt-4"
          >
            Yeni İçerik Oluştur
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="text-left p-4 font-semibold text-sm">Sıra</th>
                <th className="text-left p-4 font-semibold text-sm">Başlık</th>
                <th className="text-left p-4 font-semibold text-sm">Slug</th>
                <th className="text-left p-4 font-semibold text-sm">Tip</th>
                <th className="text-left p-4 font-semibold text-sm">SEO / Özet</th>
                <th className="text-left p-4 font-semibold text-sm">View</th>
                <th className="text-left p-4 font-semibold text-sm">Durum</th>
                <th className="text-right p-4 font-semibold text-sm">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filteredPages.map((page) => (
                <tr key={page.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="p-4 text-sm text-gray-500">{page.type === 'blog' ? 'SEO' : page.order}</td>
                  <td className="p-4">
                    <div className="font-medium">{page.title}</div>
                    {page.type === 'blog' && page.authorName ? (
                      <div className="text-xs text-gray-500 mt-1">Yazar: {page.authorName}</div>
                    ) : null}
                  </td>
                  <td className="p-4 text-sm text-gray-500 font-mono">/{page.slug}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${TYPE_COLORS[page.type]}`}>
                      {TYPE_LABELS[page.type]}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-gray-500 max-w-xs">
                    {page.type === 'blog'
                      ? (page.seoDescription || page.excerpt || 'SEO alanları henüz doldurulmadı')
                      : 'Footer içeriği'}
                  </td>
                  <td className="p-4 text-sm text-gray-600">
                    {page.type === 'blog' ? (
                      <div>
                        <div className="font-medium text-slate-900">{page.totalViews || 0}</div>
                        <div className="text-xs text-gray-500">Tekil: {page.totalUniqueViews || 0}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      page.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {page.status === 'active'
                        ? page.type === 'blog' ? 'Yayında' : 'Aktif'
                        : page.type === 'blog' ? 'Taslak' : 'Pasif'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleStatus(page)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                        title={page.status === 'active' ? 'Pasifleştir' : 'Aktifleştir'}
                      >
                        {page.status === 'active' ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        onClick={() => navigate(`/admin/pages/${page.id}/edit`)}
                        className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition text-blue-600"
                        title="Düzenle"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(page.id, page.title)}
                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition text-red-600"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
